/**
 * Cihaz kimliği.
 *
 * Tarayıcı MAC adresi, hostname ya da gerçek yerel IP vermiyor (WebRTC'nin sızdırdığı
 * yerel adres de artık her oturumda değişen bir mDNS takma adı). Giriş kayıtlarında
 * "aynı cihaz mı" sorusunu cevaplayabilmenin tek yolu, ilk girişte üretilip bu tarayıcıda
 * saklanan bir kimlik.
 *
 * Sınırı bilerek kabul ediliyor: tarayıcı verisi silinirse ya da gizli pencere
 * kullanılırsa yeni kimlik üretilir. Yani kimlik CİHAZI DEĞİL, "bu tarayıcı profilini"
 * işaretler — kimlik doğrulamada kullanılmaz, yalnızca denetim kaydını okunur kılar.
 *
 * Okunur ad artık kullanıcıdan istenmiyor: bilgisayarda Octo.Agent varsa cihaz kodu ve
 * makine adı ondan gelir (bkz. localDevice). Eskiden kullanıcı Ayarlar'dan ad veriyordu;
 * o kayıt (octo.device.name) ilk fırsatta temizlenir.
 */
import type { LocalDeviceInfo } from '@/shared/lib/octo/localDevice';

const ID_KEY = 'octo.device.id';
const LEGACY_NAME_KEY = 'octo.device.name';

/** Depolama erişimi gizli pencerede ya da site verisi kapalıyken patlayabiliyor. */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* kalıcılık yoksa kimlik bu oturuma özel kalır */
  }
}

let memoryId: string | null = null;

/**
 * Bu tarayıcının kalıcı kimliği; ilk çağrıda üretilir.
 *
 * Depolama kullanılamıyorsa kimlik yalnızca bellekte tutulur: sayfa yenilenene kadar
 * aynı kalır, sonra değişir. Kayıtta kimliğin olmaması, her seferinde yeni bir kimlik
 * yazılmasından daha az yanıltıcı olurdu; ama o durumda oturum içindeki kayıtlar da
 * birbirine bağlanamazdı.
 */
export function deviceId(): string {
  if (memoryId) return memoryId;
  try {
    localStorage.removeItem(LEGACY_NAME_KEY);
  } catch {
    /* önemsiz */
  }
  const stored = read(ID_KEY);
  if (stored) {
    memoryId = stored;
    return stored;
  }
  const created = crypto.randomUUID();
  write(ID_KEY, created);
  memoryId = created;
  return created;
}

/**
 * Giriş kaydına yazılan cihaz bölümü: ajan varsa "CİHAZKODU/Makine (kimlik)", yoksa yalnız kimlik.
 * Kimlik hep yazılır — eşleştirme kimlik üzerinden yapılır; ajan adı okunurluk içindir.
 */
export function deviceTag(local?: LocalDeviceInfo | null): string {
  const id = deviceId();
  if (!local) return id;
  const name = [local.deviceCode, local.machineName].filter((s) => s && s.trim()).join('/');
  return name ? `${name} (${id})` : id;
}
